import { useEffect, useState } from 'react'
import { clsx } from 'clsx'
import { AlertCircle, Image as ImageIcon, Loader2 } from 'lucide-react'
import { Modal, StatusIcon } from '@/components/ui'
import {
  fetchControlCardSigner,
  isMonth48WithNdt,
  publishJobControlCards,
  type ControlCardSigner,
  type PendingControlCard,
} from '@/services/jobControlCards.service'

const CONTROLLED_OPTIONS = [
  { id: 'land', label: 'Verksted/land', value: 'Forskrift Nr 1357, 1360' },
  { id: 'offshore', label: 'Offshore', value: 'Forskrift Nr 1357, 1360 & NORSOK R-003' },
] as const

const todayIso = () => new Date().toISOString().split('T')[0]

const fieldLabel = (text: string) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1.5">{text}</p>
)

interface JobControlCardsModalProps {
  isOpen: boolean
  cards: PendingControlCard[]
  onClose: () => void
  onPublished: () => void
}

const JobControlCardsModal = ({ isOpen, cards, onClose, onPublished }: JobControlCardsModalProps) => {
  const [performer, setPerformer] = useState<ControlCardSigner | null>(null)
  const [performedDate, setPerformedDate] = useState(todayIso)
  const [controlledAgainst, setControlledAgainst] = useState<string>(CONTROLLED_OPTIONS[0].value)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [failures, setFailures] = useState<{ id: string; label: string; error: string }[]>([])

  useEffect(() => {
    if (!isOpen) return
    setPerformedDate(todayIso())
    setControlledAgainst(CONTROLLED_OPTIONS[0].value)
    setProgress(null)
    setFailures([])
    fetchControlCardSigner().then(setPerformer)
  }, [isOpen])

  const publishing = progress !== null && progress.done < progress.total
  const missingResponsible = cards.filter((card) => !card.job.creator).length
  const month48Count = cards.filter(isMonth48WithNdt).length
  const missingEt = month48Count > 0 && performer !== null && !performer.has_et
  const canPublish = !publishing && cards.length > 0 && !!performer && !!performedDate

  const handlePublish = async () => {
    if (!performer) return
    setFailures([])
    setProgress({ done: 0, total: cards.length })
    const result = await publishJobControlCards({
      cards,
      performedDate,
      controlledAgainst,
      performedBy: performer,
      onProgress: (done, total) => setProgress({ done, total }),
    })
    setFailures(
      result.failed.map(({ card, error }) => ({
        id: card.assignment.id,
        label: card.equipment.serial_number || card.equipment.asset_number || 'Ukjent utstyr',
        error,
      })),
    )
    onPublished()
    if (result.failed.length === 0) onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={publishing ? () => {} : onClose} title="Generer kontrollkort" size="md">
      <div className="space-y-5">
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
          {cards.map(({ assignment, equipment, job, status }) => (
            <li key={assignment.id} className="flex items-start gap-3 px-4 py-2.5">
              <StatusIcon status={status} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {equipment.serial_number || equipment.asset_number}{' '}
                  <span className="font-normal text-slate-500">{equipment.name}</span>
                </p>
                <p className={clsx('text-xs truncate', job.creator ? 'text-slate-400' : 'text-rose-600')}>
                  {[
                    job.company_name,
                    job.order_number,
                    job.creator ? `Ordreansvarlig ${job.creator.full_name}` : 'Mangler ordreansvarlig',
                  ].filter(Boolean).join(' · ')}
                </p>
                {assignment.inspection_remarks && (
                  <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{assignment.inspection_remarks}</p>
                )}
                {isMonth48WithNdt({ assignment, status }) && (
                  <p className="mt-0.5 text-xs font-medium text-[#1E3A5F]">
                    48 måneder · ET-rapport + kontrollkort, nye kontroll- og NDT-datoer
                  </p>
                )}
                {assignment.inspection_images.length > 0 && (
                  <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                    <ImageIcon size={13} />
                    {assignment.inspection_images.length === 1
                      ? '1 bilde vedlegges'
                      : `${assignment.inspection_images.length} bilder vedlegges`}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            {fieldLabel('Utført dato')}
            <input
              type="date"
              value={performedDate}
              onChange={(e) => setPerformedDate(e.target.value)}
              className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md text-slate-800"
            />
          </div>
          <div>
            {fieldLabel('Kontrollert mot')}
            <div className="flex rounded-md bg-slate-100 p-1 h-12" role="group">
              {CONTROLLED_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setControlledAgainst(option.value)}
                  aria-pressed={controlledAgainst === option.value}
                  className={clsx(
                    'flex-1 rounded text-sm font-medium transition-colors',
                    controlledAgainst === option.value ? 'bg-white text-[#1E3A5F] shadow-sm' : 'text-slate-500',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-500">
          Utført av <span className="font-semibold text-slate-700">{performer?.full_name || '—'}</span>. Ordreansvarlig
          er den som opprettet jobben.
        </p>

        {missingResponsible > 0 && (
          <p className="text-sm text-rose-600">
            {missingResponsible} kort hoppes over fordi jobben mangler ordreansvarlig.
          </p>
        )}

        {missingEt && (
          <p className="text-sm text-rose-600">
            {month48Count} 48-måneders kort hoppes over: du mangler ET-kvalifikasjon.
          </p>
        )}

        {failures.length > 0 && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 space-y-1">
            {failures.map((f) => (
              <p key={f.id} className="flex items-start gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                {f.label}: {f.error}
              </p>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={publishing}
            className="h-12 px-5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Avbryt
          </button>
          <button
            onClick={handlePublish}
            disabled={!canPublish}
            className="h-12 px-5 rounded-md text-sm font-semibold text-white bg-[#1E3A5F] hover:bg-[#16304f] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {publishing && <Loader2 size={16} className="animate-spin" />}
            {publishing && progress
              ? `Publiserer ${Math.min(progress.done + 1, progress.total)} av ${progress.total}...`
              : `Publiser ${cards.length} kontrollkort`}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default JobControlCardsModal
